import * as React from 'react';
import { DateUtils } from 'utils/DateUtils';

// For hash routing
function getSearchParams() {
  const searchParamsUrlString = window.location.hash.split(/.(?=\?)/)[1];
  const searchParams = new URLSearchParams(searchParamsUrlString);
  return searchParams;
}

/**
 * Custom React hook to handle search params with the format: filterName[operator]value
 * Examples:
 * - carparName[ilike]ibis
 * - status[eq]started
 * - price[gte]50
 *
 * @returns {Object} Object containing searchParams, setSearchParam, removeSearchParam, clearSearchParams, and updateUrl
 */
export default function useSearchParams({
  filters, dispatchFilters, page, setPage, pageSize, setPageSize,
}) {
  // First time we render, we need to lock "search params" so filters can be updated accordingly to URL. After that, filters pushed by users should change the searchParams.
  const lockSearchParams = React.useRef(true);

  // 1️⃣ Parse URL search params on mount and when URL changes
  React.useEffect(() => {
    const parseSearchParams = () => {
      if (!filters) return;
      const searchParams = getSearchParams();
      searchParams.forEach((rawValue, key) => {
        // Parse the format: filterName[operator] or just filterName
        const match = key.match(/^([^[]+)\[?([^\]]*)\]?$/);
        const searchParamValues = rawValue.split(',');

        if (match) {
          const [, searchParamName, searchParamOperator] = match;

          const correspondingFilter = filters[searchParamName] && filters[searchParamName].operator === searchParamOperator;
          if (correspondingFilter) {
            let newValues = searchParamValues;
            newValues = searchParamValues.map((value) => {
              if (filters[searchParamName].type === 'date') return new Date(+value);
              if (filters[searchParamName].type === 'boolean') return !!value;
              return value;
            });
            dispatchFilters({ type: 'SET_FILTER_VALUES', filter: searchParamName, values: newValues });
            return;
          }

          if (searchParamName === 'page') {
            const pageParam = searchParamValues[0];
            setPage(+pageParam);
            return;
          }

          if (searchParamName === 'pageSize') {
            const pageSizeParam = searchParamValues[0];
            setPageSize(+pageSizeParam);
            return;
          }

          if (searchParamOperator === 'sort') {
            dispatchFilters({ type: 'SET_FILTER_SORT', filter: searchParamName, sort: searchParamValues[0] });
          }
        }
      });
    };

    parseSearchParams();
    const timeoutId = setTimeout(() => { lockSearchParams.current = false; }, 1000);

    // Listen to URL changes (back/forward buttons, etc.)
    const handlePopState = () => {
      parseSearchParams();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearTimeout(timeoutId);
    };
  }, []);

  // 2️⃣ Update URL when filters are updated

  React.useEffect(() => {
    const updateSearchParams = () => {
      const searchParams = getSearchParams();

      // 🔍 We reconcile filters and search params in URL
      Object.entries(filters).forEach(([filterName, {
        values,
        operator,
        sort,
        type,
        displayUrl,
      }]) => {
        if (!displayUrl) return;

        const emptyValue = !values || (Array.isArray(values) && !values[0]);
        const searchParamNameForValue = operator ? `${filterName}[${operator}]` : filterName;

        if (emptyValue) {
          searchParams.delete(searchParamNameForValue);
        } else {
          const searchParamsValues = values.map((value) => (type === 'date' && DateUtils.isDate(value) ? value.getTime() : value));
          searchParams.set(searchParamNameForValue, searchParamsValues);
        }

        const searchParamNameForSort = `${filterName}[sort]`;
        const emptySort = !sort;
        if (emptySort) {
          searchParams.delete(searchParamNameForSort);
        } else {
          searchParams.set(searchParamNameForSort, sort);
        }
      });

      searchParams.set('page', page);
      searchParams.set('pageSize', pageSize);

      const baseUrl = window.location.href.split('?')[0];
      const updatedUrl = new URL(`${baseUrl}?${searchParams.toString()}`);

      // eslint-disable-next-line no-restricted-globals
      history.pushState({}, '', updatedUrl.toString());
    };

    if (!lockSearchParams.current) updateSearchParams();
  }, [filters, page, pageSize]);
}



// WEBPACK FOOTER //
// ./routes/cpm/routes/parkings/hooks/useSearchParams.js